import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { MESSAGE_TOKENS } from './tokens';
import '../features/features-help-guide.css';

const img = (name) => (process.env.PUBLIC_URL || '') + '/help-guide/' + name;

const STEPS = [
  {
    key: 'home',
    tone: 'teal',
    image: 'mcc-help-home.png',
    titleKey: 'MessageControlHelp.Step1Title',
    titleDefault: 'Pick a location — events follow its type',
    bodyKey: 'MessageControlHelp.Step1Body',
    bodyDefault:
      'Search and select the store. Events are bound to that location’s type (Counter, Curbside, Bedside, Courier, Kiosk). Templates are saved per location, so Costco Stage and Jaimin Costco Bedside can have different copy.',
  },
  {
    key: 'sms',
    tone: 'orange',
    image: 'mcc-help-sms.png',
    titleKey: 'MessageControlHelp.Step2Title',
    titleDefault: 'SMS is composed in HTML, sent as plain text',
    bodyKey: 'MessageControlHelp.Step2Body',
    bodyDefault:
      'Use Design to write the message. Gray chips are tokens — they cannot be deleted. The dark preview on the right is what the patient actually receives as an SMS. Switch to HTML source only if you need to inspect markup.',
  },
  {
    key: 'tokens',
    tone: 'purple',
    image: 'mcc-help-tokens.png',
    titleKey: 'MessageControlHelp.Step3Title',
    titleDefault: 'What is a token?',
    bodyKey: 'MessageControlHelp.Step3Body',
    bodyDefault:
      'A token is a merge field such as First Name. It is a placeholder, not the words the patient reads. At send time the system replaces each token with live data: the patient’s first name, this store’s hours, the pickup code, and so on. Click Token in the toolbar and choose the field you want — it does not always insert First Name.',
  },
  {
    key: 'email',
    tone: 'green',
    image: 'mcc-help-email.png',
    titleKey: 'MessageControlHelp.Step4Title',
    titleDefault: 'Email is HTML, including images',
    bodyKey: 'MessageControlHelp.Step4Body',
    bodyDefault:
      'The Email tab sends sanitized HTML. Use the image button to upload a JPEG, PNG, GIF, or WEBP. The preview on the right shows the rendered email, including images. Subject still uses tokens such as {{Account Name}}.',
  },
  {
    key: 'notes',
    tone: 'coral',
    image: 'mcc-help-notes.png',
    titleKey: 'MessageControlHelp.Step5Title',
    titleDefault: 'Custom notes and publish',
    bodyKey: 'MessageControlHelp.Step5Body',
    bodyDefault:
      'Custom notes are the location-specific line inserted into SMS, email, and the mobile page. Keep one default and up to five alternates. Save as draft to keep work in progress. Publish makes the next patient send use this template. Restore default returns the iLocal skeleton.',
  },
  {
    key: 'audit',
    tone: 'teal',
    titleKey: 'MessageControlHelp.Step6Title',
    titleDefault: 'Who created, edited, or deleted a template',
    bodyKey: 'MessageControlHelp.Step6Body',
    bodyDefault:
      'The list shows Created by and Modified by. Open Audit history on a row for create, edit, publish, restore default, and delete. The same entries appear on the Audit Trail report under Studio.',
  },
];

export const MessageControlHelpGuide = ({
  t,
  open,
  onClose,
  paymentEnabled = true,
}) => (
  <Dialog
    open={!!open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    scroll="paper"
    className="features-help-dialog"
    style={{ zIndex: 1500 }}
    PaperProps={{ className: 'features-help-paper' }}
  >
    <div className="features-help-hero">
      <IconButton
        aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
        onClick={onClose}
        className="features-help-close"
      >
        <CloseIcon />
      </IconButton>
      <p className="features-help-eyebrow">
        {t('MessageControlHelp.Eyebrow', { defaultValue: 'Studio' })}
      </p>
      <h2>
        {t('MessageControlHelp.Title', {
          defaultValue: 'Studio guide',
        })}
      </h2>
      <p>
        {t('MessageControlHelp.Subtitle', {
          defaultValue:
            'How to edit SMS, email, and mobile copy with tokens, custom notes, and a live preview of what the patient receives.',
        })}
      </p>
    </div>
    <DialogContent className="features-help-body">
      {STEPS.map((step, index) => (
        <section
          key={step.key}
          className={`features-help-step is-${step.tone}`}
        >
          <div className="features-help-step-copy">
            <span className="features-help-step-num">{index + 1}</span>
            <h3>{t(step.titleKey, { defaultValue: step.titleDefault })}</h3>
            <p>{t(step.bodyKey, { defaultValue: step.bodyDefault })}</p>
          </div>
          {step.image ? (
            <div className="features-help-step-media">
              <img
                src={img(step.image)}
                alt={t(step.titleKey, { defaultValue: step.titleDefault })}
              />
            </div>
          ) : null}
        </section>
      ))}
      <div className="features-help-tips mcc-help-tokens">
        <h3>
          {t('MessageControlHelp.TokenTableTitle', {
            defaultValue: 'Token reference — filled at send time',
          })}
        </h3>
        <p className="mcc-help-token-intro">
          {t('MessageControlHelp.TokenTableIntro', {
            defaultValue:
              'Gray chips in the editor are locked. The preview on the right already shows sample values (Maria, ABC123, …). Patients see the real values when the order is delivered.',
          })}
        </p>
        <table className="mcc-help-token-table">
          <thead>
            <tr>
              <th>
                {t('MessageControlHelp.TokenCol', { defaultValue: 'Token' })}
              </th>
              <th>
                {t('MessageControlHelp.MeansCol', { defaultValue: 'What it means' })}
              </th>
              <th>
                {t('MessageControlHelp.ExampleCol', { defaultValue: 'Preview example' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {MESSAGE_TOKENS.filter(
              (token) => paymentEnabled || token.name !== 'Amount'
            ).map((token) => (
              <tr key={token.name}>
                <td>
                  <code>{`{{${token.name}}}`}</code>
                </td>
                <td>{token.hint}</td>
                <td>{token.example}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul>
          <li>
            {t('MessageControlHelp.Tip1', {
              defaultValue:
                'Do not type a patient’s real name into the template. Use the First Name token so every order gets the correct person.',
            })}
          </li>
          <li>
            {t('MessageControlHelp.Tip2', {
              defaultValue:
                'Amount and Link TTL are omitted at send time when there is nothing to pay or the link does not expire.',
            })}
          </li>
          <li>
            {t('MessageControlHelp.Tip3', {
              defaultValue:
                'STOP is required on SMS and cannot be removed. Email can include a brand image from the toolbar.',
            })}
          </li>
          <li>
            {t('MessageControlHelp.Tip4', {
              defaultValue:
                'Staff choose a custom note on Deliver. That note fills the custom-note region in SMS, email, and the mobile page for that order only.',
            })}
          </li>
        </ul>
      </div>
    </DialogContent>
    <DialogActions className="features-help-actions">
      <Button onClick={onClose} sx={{ textTransform: 'none' }}>
        {t('MessageControl.Close', { defaultValue: 'Close' })}
      </Button>
    </DialogActions>
  </Dialog>
);

export const MessageControlHelpButton = ({
  t,
  size = 'small',
  paymentEnabled = true,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <React.Fragment>
      <Button
        variant="outlined"
        size={size}
        startIcon={<HelpOutlineIcon />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#7dd3fc',
          color: '#0369a1',
          background: '#fff',
          '&:hover': {
            borderColor: '#00a7e1',
            background: 'rgba(0, 167, 225, 0.06)',
          },
        }}
      >
        {t('MessageControlHelp.OpenGuide', { defaultValue: 'Help guide' })}
      </Button>
      <MessageControlHelpGuide
        t={t}
        open={open}
        paymentEnabled={paymentEnabled}
        onClose={() => setOpen(false)}
      />
    </React.Fragment>
  );
};

export default MessageControlHelpGuide;



// WEBPACK FOOTER //
// ./src/components/message-control-center/MessageControlHelpGuide.js